/**
 * Canonical jurisdiction and entity records.
 *
 * Three layers:
 *   national      — information identical across all jurisdictions, stored once
 *   jurisdiction  — entity-agnostic identity and access, 51 records
 *   entity        — everything that differs by entity type, 51 x 2 records
 *
 * Normalisation is internal to the data architecture. `resolveJurisdiction`
 * composes the three layers back into a complete view, so nothing is removed
 * from the employee reading experience.
 *
 * Stage 1: structure only. No jurisdiction data is expressed in this file.
 */

import type { StateInfo } from "../types";
import type {
  Cadence,
  Charge,
  ChannelFee,
  Channel,
  DueDateRule,
  SharesModel,
} from "./fees";
import type { Evidence, Fact, SourceRef } from "./status";

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export type EntityType = "llc" | "corp";

export const ENTITY_TYPES = ["llc", "corp"] as const satisfies readonly EntityType[];

/** Two-letter postal code. The stable primary key the legacy dataset lacks. */
export type JurisdictionId = string;

/** The filing agency is not a Secretary of State everywhere. */
export type AgencyType =
  | "secretary_of_state"
  | "secretary_of_the_state"
  | "corporation_commission"
  | "department_of_state"
  | "division_of_corporations"
  /** Maryland's State Department of Assessments and Taxation. */
  | "sdat"
  /** The District's Department of Licensing and Consumer Protection. */
  | "dlcp"
  | "other";

export const AGENCY_TYPES = [
  "secretary_of_state",
  "secretary_of_the_state",
  "corporation_commission",
  "department_of_state",
  "division_of_corporations",
  "sdat",
  "dlcp",
  "other",
] as const satisfies readonly AgencyType[];

/**
 * Entity-agnostic jurisdiction record.
 *
 * `geoKey` is load-bearing: the map joins geometry, hover glow and fly-to on the
 * normalised display name, so this must equal `normalizeStateName()`'s output
 * verbatim. `displayName` is what the current UI shows and keys on.
 */
export type JurisdictionRecord = {
  id: JurisdictionId;
  /** Formal name, e.g. "District of Columbia". */
  name: string;
  /** Name as the current application displays and keys it, e.g. "Washington DC". */
  displayName: string;
  /** MUST equal normalizeStateName() output for this jurisdiction. */
  geoKey: string;
  /** Other names this jurisdiction is referred to by, so imports match one record. */
  aliases: string[];
  /**
   * The filing agency.
   *
   * OPTIONAL because it is genuinely not available yet: the application's
   * existing jurisdiction data carries portal URLs but no agency name or type,
   * and an agency identity is not something to infer from a URL. It is authored
   * when a trusted source provides it, and its absence is explicit rather than
   * filled with a guess.
   */
  agency?: { name: string; type: AgencyType };
  portals: {
    businessSearch?: string;
    extraLinks: { label: string; url: string }[];
    vpnRequired: boolean;
    channels: Channel[];
  };
  /**
   * Portal and card pass-throughs that apply to EVERY entity type here.
   *
   * Only a charge verified as jurisdiction-wide belongs on this layer, because
   * anything recorded here reaches the LLC view and the corporation view alike.
   * A charge established from one entity type's source alone belongs on that
   * entity's record instead — see `EntityRecord.channelFees`.
   */
  channelFees: ChannelFee[];
  /** Jurisdiction-level notes that are not entity-specific. */
  notes: Fact[];
};

// ---------------------------------------------------------------------------
// National layer
// ---------------------------------------------------------------------------

/**
 * A block of information identical across every jurisdiction, held once and
 * composed into each jurisdiction view.
 */
export type NationalSection = {
  id: string;
  label: string;
  facts: Fact[];
  charges?: Charge[];
};

export type ServiceTier = {
  id: string;
  name: string;
  price: number;
  fact: Fact<never>;
};

export type NationalRecord = {
  entityType: EntityType;
  /** Our own service pricing, which does not vary by jurisdiction. */
  serviceTiers: ServiceTier[];
  /** Addressable by id from an entity record's `*Ref` fields. */
  sections: Record<string, NationalSection>;
  /** The add-on catalogue every jurisdiction starts from. */
  addOnBaseline: { count: number; products: string[] };
  /** Things we do not sell, recorded so nobody re-adds them as a gap. */
  notOffered: Fact[];
  /** Programme-level delivery risks. */
  deliveryRisks: Fact[];
  sourceRef: SourceRef;
};

// ---------------------------------------------------------------------------
// Entity layer
// ---------------------------------------------------------------------------

export type AvailabilityValue = "selectable_at_intake" | "not_offered";

export type FulfillmentValue =
  | "manual_salesforce_case"
  | "rai_vendor_api"
  | "in_house_automation"
  | "other";

export type RegisteredAgentValue = "in_house" | "rai_vendor_network" | "other";

export type PrivacyValue = "private" | "public_at_filing" | "public_near_filing" | "unknown";

export type CloseCorpValue = "yes" | "no" | "legacy_only" | "not_verified";

/**
 * How a jurisdiction's add-on catalogue differs from the national baseline.
 * Stored as a delta so the baseline is never restated 51 times.
 */
export type ServiceDelta = {
  /**
   * Reference into a national record's `addOnBaseline`. Optional so a
   * not-yet-migrated record can omit it rather than point at a baseline that
   * has not been authored.
   */
  baselineRef?: string;
  /** Total available in this jurisdiction, as the source states it. */
  count?: number;
  added: string[];
  /** Products absent here. A catalogue defect rather than a state restriction. */
  missing: string[];
  fact: Fact<never>;
};

/**
 * A highlighted item: the reference documents' shaded boxes and badge chips.
 * `kind` uses the source documents' own families so import never invents one.
 */
export type AdvisoryKind =
  | "publication_requirement"
  | "state_business_license"
  | "initial_report"
  | "first_report_timing"
  | "franchise_or_annual_tax"
  | "per_member_fee"
  | "renewal_date_discrepancy"
  | "known_service_gap"
  | "open_research_item"
  | "no_par_trap"
  | "fee_depends_on_shares"
  | "source_caveat"
  | "aggregator_warning"
  | "other";

export const ADVISORY_KINDS = [
  "publication_requirement",
  "state_business_license",
  "initial_report",
  "first_report_timing",
  "franchise_or_annual_tax",
  "per_member_fee",
  "renewal_date_discrepancy",
  "known_service_gap",
  "open_research_item",
  "no_par_trap",
  "fee_depends_on_shares",
  "source_caveat",
  "aggregator_warning",
  "other",
] as const satisfies readonly AdvisoryKind[];

export type Advisory = {
  id: string;
  title: string;
  kind: AdvisoryKind;
  fact: Fact;
};

/**
 * A group of charges that is either held nationally and referenced, or stated
 * locally. Exactly one of `ref` and `charges` is set.
 */
export type ChargeGroup = { ref: string; charges?: never } | { ref?: never; charges: Charge[] };

export type EntityRecord = {
  jurisdictionId: JurisdictionId;
  entityType: EntityType;

  availability: Fact<AvailabilityValue>;
  /** How the filing is actually fulfilled. */
  fulfillment: Fact<FulfillmentValue>;

  formation: {
    /** Reference to national service tiers, which do not vary by jurisdiction. */
    serviceTiersRef?: string;
    /** The state's own filing fee. */
    stateFee?: Charge;
    /** Mandatory or conditional charges levied alongside formation. */
    companions: Charge[];
  };

  registeredAgent: Fact<RegisteredAgentValue>;

  renewal: {
    cadence: Fact<Cadence>;
    dueDate?: Fact<DueDateRule>;
    charges: Charge[];
    /** Where we do not file and the client must, recorded rather than left blank. */
    referral?: Fact;
  };

  /** Nationally flat in both reference documents; referenced, not restated. */
  amendments: ChargeGroup;
  certificates: ChargeGroup;
  taxElections: ChargeGroup;
  documents: ChargeGroup;

  services: ServiceDelta;

  /** State-specific requirements. Always present; a deliberate N/A where none apply. */
  requirements: Fact[];
  advisories: Advisory[];

  /** Charges that recur outside the renewal filing itself. */
  recurring: Charge[];

  /**
   * Portal and card pass-throughs established for THIS entity type only.
   *
   * The same `ChannelFee` shape as the shared layer — not a second model — but
   * scoped to one entity type, because a state portal charge is not
   * automatically the same for a corporation filing as for an LLC one, and our
   * two reference documents establish them independently. A charge read out of
   * the corporation reference alone belongs here, on the corporation record;
   * putting it on the shared jurisdiction record would assert it of the LLC
   * view too, on evidence that does not cover the LLC.
   *
   * Resolution is by stable id: an entry here whose id matches a shared entry
   * REPLACES it for this entity type; an entry with a new id is added. So the
   * same source charge renders exactly once, and an entity type can correct a
   * jurisdiction-wide figure without editing the shared record.
   */
  channelFees?: ChannelFee[];

  /** Corporations only. Absent on LLC records. */
  shares?: SharesModel;
  privacy?: Fact<PrivacyValue>;
  closeCorp?: Fact<CloseCorpValue>;
  /** Rollout tier. Engineering state, so internal-only. */
  rollout?: Fact<string>;

  verification: {
    /** Evidence grade per named aspect, as the source records it. */
    grades: Record<string, Evidence>;
    caveats: string[];
  };
  sourceRef: SourceRef;

  /**
   * Explicit projection into the legacy `StateInfo` shape.
   *
   * Present means this jurisdiction is migrated and the legacy dataset is
   * generated from canonical data. Absent means it is not yet migrated and the
   * build serves it from the legacy dataset unchanged. This is what makes
   * partial migration safe.
   */
  legacyProjection?: LegacyProjection;

  /**
   * TRANSITIONAL COMPATIBILITY SLOT — deliberately isolated here rather than
   * loosened into the canonical fields above.
   *
   * Facts carried verbatim out of the legacy dataset, with `display` holding
   * the original string and no interpretation applied. They exist so a
   * partially-migrated record can still project a complete legacy view, and so
   * the round trip can be proven on real data without any field being parsed
   * or inferred. Each batch replaces these with real canonical facts; when a
   * jurisdiction is fully migrated this array is empty or absent.
   */
  legacyFacts?: Fact[];
};

// ---------------------------------------------------------------------------
// Legacy projection
// ---------------------------------------------------------------------------

export type LegacyFieldName = keyof StateInfo;

/**
 * Where a legacy field's content comes from. Entries reference facts by id
 * rather than embedding them, so canonical records stay the single authority
 * and nothing is duplicated in storage.
 */
export type LegacyProjectionEntry =
  /** A single display string from one fact. */
  | { from: "fact"; factId: string }
  /** A `Subtopic[]` field, built from one fact per labelled part. */
  | { from: "subtopics"; parts: { label: string; factId: string }[] }
  /** A `string[]` field such as `requirements`. */
  | { from: "list"; factIds: string[] }
  /** A constant, for fields with no canonical counterpart. */
  | { from: "literal"; value: string };

export type LegacyProjectionField = {
  field: LegacyFieldName;
  source: LegacyProjectionEntry;
};

/**
 * An ORDERED projection. Order matters: the existing dataset uses three
 * different key orders across its 51 records, so reproducing it without
 * incidental change requires per-jurisdiction ordering rather than one global
 * field order.
 */
export type LegacyProjection = {
  /** Legacy fields in the exact order they must be serialised. */
  fields: LegacyProjectionField[];
};

// ---------------------------------------------------------------------------
// Dataset
// ---------------------------------------------------------------------------

/**
 * The canonical dataset. Partially populated during migration: a jurisdiction
 * may exist in `jurisdictions` with no entity record, or with an LLC record and
 * no corporation record.
 */
export type CanonicalDataset = {
  version: number;
  jurisdictions: Record<JurisdictionId, JurisdictionRecord>;
  national: { llc?: NationalRecord; corp?: NationalRecord };
  entities: {
    llc: Record<JurisdictionId, EntityRecord>;
    corp: Record<JurisdictionId, EntityRecord>;
  };
};

export function emptyDataset(version = 1): CanonicalDataset {
  return {
    version,
    jurisdictions: {},
    national: {},
    entities: { llc: {}, corp: {} },
  };
}

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------

/**
 * A reference into the national layer, written `national/<entityType>#<sectionId>`.
 * For service baselines and tiers the section id is `addOnBaseline` / `serviceTiers`.
 */
export type ParsedRef = { entityType: EntityType; sectionId: string };

const REF_PATTERN = /^national\/(llc|corp)#([A-Za-z0-9_.-]+)$/;

export function parseRef(ref: string): ParsedRef | null {
  const m = REF_PATTERN.exec(ref);
  if (!m) return null;
  return { entityType: m[1] as EntityType, sectionId: m[2] };
}

export function formatRef(entityType: EntityType, sectionId: string): string {
  return `national/${entityType}#${sectionId}`;
}

/** The source document a given entity type is permitted to cite. */
export const ENTITY_SOURCE_DOCUMENT = {
  llc: "LLC_REFERENCE",
  corp: "CORP_REFERENCE",
} as const;

/** The source document a given entity type must never cite. */
export const FORBIDDEN_SOURCE_DOCUMENT = {
  llc: "CORP_REFERENCE",
  corp: "LLC_REFERENCE",
} as const;
